import {
  addDays,
  differenceInDays,
  format,
  isAfter,
  isBefore,
  isSameDay
} from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components/native';

import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import { useQueryStudyParticipantData } from '../../../../hooks/study-participant/useQueryStudyParticipantData';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import IconButton from '../../../../components/IconButton';
import { Feather, Ionicons, MaterialIcons } from '@expo/vector-icons';
import Card from '../../../../components/Card';
import LineChart, {
  getHoursLabel
} from '../../../../components/charts/LineChart';
import { Caption, H4, H5 } from '../../../../components/Typography';
import Spacer from '../../../../components/Spacer';
import { Modal, Platform, SafeAreaView, View } from 'react-native';
import {
  enableLandscapeOrientationLock,
  enablePortraitOrientationLock
} from '../../../../tools/DeviceHelper';
import HeaderTabView from '../../../../components/HeaderTabView';
import SelectField, {
  SelectOption
} from '../../../../components/form/SelectField';
import {
  OuraSpecificDataType,
  UserAttribute,
  UserAttributeInputType,
  UserAttributeSource,
  UserAttributeType
} from '../../../../models/UserAttribute';
import { useFindUserAttributes } from '../../../../hooks/user/useFindUserAttributes';
import { useUserContext } from '../../../../context/UserContext';
import Colors from '../../../../constants/Colors';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { measurements } from '../../../../components/MyDataList';
import { UserUnitSystem } from '../../../../models/User';
import { isArray } from 'lodash';

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
`;

const StyledView = styled(Card)`
  position: relative;
  width: 100%;
`;

const StyledIconButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  position: absolute;
  right: 20px;
  top: 12px;
  z-index: 1;
`;

const StyledSyncText = styled(Caption)`
  font-family: 'WorkSans-Medium';
  font-size: 14px;
  line-height: 22px;
  color: #949aad;
`;

const StyledTitleView = styled.View`
  width: 100%;
  padding: 16px 40px 0px 20px;
`;

const StyledHorizontalView = styled.View`
  flex-direction: row;
  align-items: center;
`;

const StyledBottomView = styled.View`
  width: 100%;
  align-items: center;
  margin-top: 14px;
  margin-bottom: 20px;
`;

const StyledHeaderView = styled.View`
  width: 100%;
  height: 18px;
  margin-top: 12px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledBackIconButton = styled(IconButton)`
  width: 60px;
  height: 40px;
  position: absolute;
  left: -20px;
  top: -10px;
  z-index: 1;
`;

const StyledModalInfoView = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding: 8px 12px 0px 12px;
  width: 100%;
  height: 46px;
`;

const StyledModalInfoTextView = styled.View`
  height: 100%;
`;

const StyledSelectFieldView = styled.View`
  min-width: 104px;
`;

export interface WearableDataLineChartComparison {
  name?: string;
  unit?: string;
  comparisonType?: ComparisonType;
  entityId?: string;
  columnName?: string;
  userAttributeType?: UserAttributeType;
  userAttributeSource?: UserAttributeSource;
}

export interface WearableDataLineChartCardProps {
  title?: string;
  comparisons?: WearableDataLineChartComparison[];
  displayDayCount?: number;
  landscapeDayCount?: number;
  portraitDayCount?: number;
  wearableDataSources?: WearableDataSourceConfig;
  portraitWearableDataSource?: string;
  variant?: 'study' | 'measurement';
  latestAttributes: UserAttribute[];
  connectedWearableSources: UserAttributeSource[];
  onSelectSource?: (
    source: UserAttributeSource,
    subSource?: string,
    sourceName?: string
  ) => void;
  embedded?: boolean;
  loading?: boolean;
}

enum ComparisonType {
  Wearable = 'wearable',
  Entity = 'entity'
}

enum WearableTypeUnits {
  Sleep = 'h',
  'Blood Glucose' = 'mmol/L',
  'Blood Pressure' = 'mmHg',
  Steps = 'steps',
  'Body Temperature' = '°C',
  'Calories Burned' = 'cal',
  'Calories Consumed' = 'cal',
  'Body Mass Index' = '',
  'Body Fat Percentage' = '%',
  Weight = 'kg',
  'Active Energy Burned' = 'cal',
  'Basal Energy Burned' = 'cal',
  'Blood Alcohol Content' = '%',
  Carbohydrates = 'g',
  'Energy Consumed' = 'cal',
  Height = 'cm',
  'Lean Body Mass' = 'kg',
  'Protein Consumed' = 'g',
  'Total Fat Consumed' = 'g',
  'Heart Rate Variability' = 'ms',
  'Heart Rate' = 'bpm',
  'Oxygen Saturation' = '%',
  'Resting Heart Rate' = 'bpm',
  // Oura additional attributes
  'Respiratory Rate' = 'breaths/min',
  'Lowest Heart Rate' = 'bpm',
  'Sleep Efficiency' = '%',
  'Awake Time' = 'min',
  'Deep Sleep Duration' = 'min',
  'Sleep Latency' = 'min',
  'Light Sleep Duration' = 'min',
  'REM Sleep Duration' = 'min',
  'Temperature Deviation' = '°C',
  'Temperature Trend Deviation' = '°C',
  'Active Calories' = 'cal',
  'High Activity Time' = 'min',
  'Low Activity Time' = 'min',
  'Non-wear Time' = 'min',
  'Sedentary Time' = 'min',
  'SPO2 Nightly Disturbances' = '',
  Workout = 'cal'
}

type DataSourceConfig = {
  dataSource: string;
  category?: string;
};

type WearableDataSourceConfig = {
  healthKit: DataSourceConfig;
  googleFit: DataSourceConfig;
  healthConnect: DataSourceConfig;
  fitbit: DataSourceConfig;
  garmin: DataSourceConfig;
  oura: DataSourceConfig;
};

type WearableSearchConfig = {
  config: DataSourceConfig;
  source: UserAttributeSource;
  unit?: string;
  created?: Date;
  subSource?: string;
  sourceName?: string;
};

export type SelectedWearableConfig = {
  source: UserAttributeSource;
  sourceName: string;
  subSource?: string;
  type: UserAttributeType;
};

const createUniqueSet = (configs: { sourceName: string; source: string }[]) => {
  const occurrences = new Map<string, number>(); //track occurrences of sourceName
  const duplicates = new Set<string>(); // track duplicates

  // Identify duplicates and populate occurrences map
  configs.forEach(({ sourceName }) => {
    const count = occurrences.get(sourceName) || 0;
    occurrences.set(sourceName, count + 1);
    if (count + 1 > 1) {
      duplicates.add(sourceName);
    }
  });

  // Append source to duplicates and return updated configs
  const updatedConfigs = configs.map(({ sourceName, source }) => {
    if (duplicates.has(sourceName) && sourceName !== source) {
      return `${sourceName} (${source})`;
    }
    return sourceName;
  });

  return updatedConfigs;
};

const roundOffSteps = (num: number) => {
  const thresholds = [10, 100, 1000, 10000];
  for (let i = 0; i < thresholds.length; i++) {
    if (num < thresholds[i]) {
      return thresholds[i];
    }
  }
  return Math.round(num / 100) * 100;
};

export const WearableDataLineChartCard: React.FC<
  ScreenComponentProps<WearableDataLineChartCardProps>
> = ({
  title = '',
  studyParticipant,
  embedded,
  comparisons = [],
  portraitDayCount = 0,
  landscapeDayCount = 0,
  wearableDataSources = {
    healthKit: { dataSource: '' },
    googleFit: { dataSource: '' },
    healthConnect: { dataSource: '' },
    fitbit: { dataSource: '' },
    garmin: { dataSource: '' },
    oura: { dataSource: '' }
  },
  variant = 'study',
  latestAttributes,
  connectedWearableSources = [],
  loading,
  onSelectSource,
  ...props
}) => {
  const { user } = useUserContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<number>();
  const { queryResults, loadingQuery, loadingQueryError, queryData } =
    useQueryStudyParticipantData(studyParticipant?.id || 0);
  const { findUserAttributes, loadingUserAttributes } = useFindUserAttributes();
  const [userAttributeDataList, setUserAttributeDataList] = useState<
    { attributes: UserAttribute[] }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [compareAttributes, setCompareAttributes] = useState<UserAttribute[]>(
    []
  );
  const previousConfigOrderRef =
    useRef<{ sourceName: string; source: UserAttributeSource }[]>();
  const { maxStartDate, portraitStartDate, landscapeStartDate } =
    useMemo(() => {
      const portraitStartDate = addDays(new Date(), 1 - portraitDayCount);
      const landscapeStartDate = addDays(new Date(), 1 - landscapeDayCount);
      return {
        maxStartDate: isAfter(portraitStartDate, landscapeStartDate)
          ? landscapeStartDate
          : portraitStartDate,
        portraitStartDate,
        landscapeStartDate
      };
    }, [landscapeDayCount, portraitDayCount]);

  // convert metric to imperial
  const convertToImperial: (
    attributes: UserAttribute[],
    type?: string
  ) => UserAttribute[] = (attributes, type) => {
    if (type) {
      let conversion: 'fahrenheit' | 'pound' | 'feet' | undefined;
      switch (type) {
        case '°C':
          conversion = 'fahrenheit';
          break;
        case 'kg':
          conversion = 'pound';
          break;
        case 'cm':
          conversion = 'feet';
          break;
      }
      return attributes.map(attribute => ({
        ...attribute,
        numberValue:
          typeof attribute.numberValue !== 'undefined'
            ? conversion === 'pound'
              ? attribute.numberValue * 2.2046226218
              : conversion === 'fahrenheit'
              ? (attribute.numberValue * 9) / 5 + 32
              : conversion === 'feet'
              ? attribute.numberValue / 30.48
              : attribute.numberValue
            : attribute.numberValue
      }));
    }
    return attributes;
  };

  const getConvertedUnit: (unit?: string) => string = unit => {
    let convertedUnit = unit;
    if (user?.unitSystem === UserUnitSystem.Imperial) {
      switch (unit) {
        case '°C':
          convertedUnit = '°F';
          break;
        case 'kg':
          convertedUnit = 'lb';
          break;
        case 'cm':
          convertedUnit = 'ft';
          break;
      }
    }
    return convertedUnit || '';
  };

  // generate sample data
  const sampleData = useCallback(() => {
    const sampleDataPoints: { x: number; y: number }[] = [];
    let max = 0;
    for (let i = 0; i < portraitDayCount; i++) {
      sampleDataPoints.push({ x: i, y: 4 + i });
      max = 6 + i;
    }
    return {
      wearableDataPoints: sampleDataPoints,
      maxY: max,
      isOxygenSaturation: false,
      syncedDate: 'Last sync: ' + format(new Date(), 'MMM d, yyyy h:mm a'),
      studyStartXPoint: undefined,
      roundValuesYAxis: true
    };
  }, [portraitDayCount]);

  const wearableSearchConfigs = useMemo(() => {
    if (connectedWearableSources.length === 0 || latestAttributes.length === 0)
      return [];
    const configs: WearableSearchConfig[] = [];
    [...latestAttributes]
      .sort((objA, objB) => {
        if (!previousConfigOrderRef.current) return 0;
        // This is to avoid sudden change to the order of Header Tabs
        // when the most recent data comes from a different wearable
        const sourceNameA = objA.sourceName || objA.source;
        const sourceNameB = objB.sourceName || objB.source;
        if (
          variant === 'measurement' &&
          previousConfigOrderRef?.current &&
          sourceNameA &&
          sourceNameB
        ) {
          if (
            previousConfigOrderRef.current.findIndex(
              config =>
                config.sourceName === sourceNameA &&
                config.source === objA.source
            ) === -1 ||
            previousConfigOrderRef.current.findIndex(
              config =>
                config.sourceName === sourceNameB &&
                config.source === objB.source
            ) === -1
          ) {
            return -1;
          }
          return (
            previousConfigOrderRef.current.findIndex(
              config =>
                config.sourceName === sourceNameA &&
                config.source === objA.source
            ) -
            previousConfigOrderRef.current.findIndex(
              config =>
                config.sourceName === sourceNameB &&
                config.source === objB.source
            )
          );
        }
        return isAfter(new Date(objA.endDate), new Date(objB.endDate)) ? -1 : 1;
      })
      .forEach(attribute => {
        if (
          configs.some(
            config =>
              config.source === attribute.source &&
              config.subSource === attribute.subSource &&
              config.sourceName === attribute.sourceName
          )
        ) {
          return;
        }
        switch (attribute.source) {
          case UserAttributeSource.HealthKit:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.HealthKit
              ) &&
              wearableDataSources.healthKit?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.healthKit,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          case UserAttributeSource.GoogleFit:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.GoogleFit
              ) &&
              wearableDataSources.googleFit?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.googleFit,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          case UserAttributeSource.HealthConnect:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.HealthConnect
              ) &&
              wearableDataSources.healthConnect?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.healthConnect,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          case UserAttributeSource.Fitbit:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.Fitbit
              ) &&
              wearableDataSources.fitbit?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.fitbit,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          case UserAttributeSource.Garmin:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.Garmin
              ) &&
              wearableDataSources.garmin?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.garmin,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          case UserAttributeSource.Oura:
            if (
              connectedWearableSources.find(
                source => source === UserAttributeSource.Oura
              ) &&
              wearableDataSources.oura?.dataSource === attribute.type
            ) {
              configs.push({
                config: wearableDataSources.oura,
                source: attribute.source,
                unit: attribute.unit,
                created: attribute.created,
                subSource: attribute.subSource,
                sourceName: attribute.sourceName
              });
            }
            break;
          default:
            break;
        }
      });
    return configs;
  }, [
    connectedWearableSources,
    latestAttributes,
    variant,
    wearableDataSources.fitbit,
    wearableDataSources.garmin,
    wearableDataSources.googleFit,
    wearableDataSources.healthKit,
    wearableDataSources.oura
  ]);

  const headerTabList = useMemo(() => {
    const list = wearableSearchConfigs.map(value =>
      String(value.sourceName || value.source)
    );
    connectedWearableSources.forEach(c => {
      if (!wearableSearchConfigs.find(wearable => wearable.source === c)) {
        list.push(c === UserAttributeSource.HealthKit ? 'Health' : c);
      }
    });
    const headerTabSet = list.map((value, index) => ({
      sourceName: value,
      source:
        index < wearableSearchConfigs.length
          ? wearableSearchConfigs[index].source ===
            UserAttributeSource.HealthKit
            ? 'Health'
            : wearableSearchConfigs[index].source
          : ''
    }));
    return createUniqueSet(headerTabSet);
  }, [connectedWearableSources, wearableSearchConfigs]);

  const [selectedWearableConfigIndex, setSelectedWearableConfigIndex] =
    useState(0);
  const [selectedWearableConfigs, setSelectedWearableConfigs] = useState<
    SelectedWearableConfig[]
  >([]);
  const [isHeaderTabChanged, setIsHeaderTabChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!previousConfigOrderRef.current) {
      setIsLoading(true);
      const getSelectedSource = async () => {
        const wearableConfigs = await AsyncStorage.getItem('wearableConfigs');
        if (wearableConfigs) {
          const parsedWearableConfigs: SelectedWearableConfig[] =
            JSON.parse(wearableConfigs);
          const matchingWearableConfig = parsedWearableConfigs.find(
            config =>
              config.type === wearableSearchConfigs[0]?.config?.dataSource
          );
          if (matchingWearableConfig) {
            const wearableConfigIndex = headerTabList.findIndex(
              config =>
                config === matchingWearableConfig.sourceName ||
                config === matchingWearableConfig.source
            );
            onSelectSource?.(
              wearableSearchConfigs[wearableConfigIndex]?.source,
              wearableSearchConfigs[wearableConfigIndex]?.subSource,
              wearableSearchConfigs[wearableConfigIndex]?.sourceName
            );
            setSelectedWearableConfigIndex(wearableConfigIndex);
          } else {
            onSelectSource?.(
              wearableSearchConfigs[0].source,
              wearableSearchConfigs[0].subSource,
              wearableSearchConfigs[0].sourceName
            );
          }
          setSelectedWearableConfigs(parsedWearableConfigs);
        } else {
          onSelectSource?.(
            wearableSearchConfigs[0].source,
            wearableSearchConfigs[0].subSource,
            wearableSearchConfigs[0].sourceName
          );
        }
        setIsLoading(false);
      };
      getSelectedSource();
      previousConfigOrderRef.current = wearableSearchConfigs.map(config => ({
        sourceName: config.sourceName || config.source,
        source: config.source
      }));

      connectedWearableSources.forEach(source => {
        if (
          !wearableSearchConfigs.find(wearable => wearable.source === source)
        ) {
          previousConfigOrderRef.current?.push({
            sourceName:
              source === UserAttributeSource.HealthKit ? 'Health' : source,
            source
          });
        }
      });
    }
  }, [connectedWearableSources, onSelectSource, wearableSearchConfigs]);

  useEffect(() => {
    if (wearableSearchConfigs.length > 0 && maxStartDate) {
      (async () => {
        const userAttributeList: { attributes: UserAttribute[] }[] =
          wearableSearchConfigs.map(() => ({ attributes: [] }));
        for (let i = 0; i < wearableSearchConfigs.length; i++) {
          const userAttributes =
            (await findUserAttributes(
              studyParticipant?.userId
                ? studyParticipant.userId
                : variant === 'measurement'
                ? user?.id || -1
                : -1,
              wearableSearchConfigs[i]?.config?.dataSource,
              wearableSearchConfigs[i].source,
              wearableSearchConfigs[i].sourceName,
              undefined,
              undefined,
              undefined,
              Math.max(landscapeDayCount, portraitDayCount)
            )) || [];
          userAttributeList[i].attributes =
            user?.unitSystem === UserUnitSystem.Imperial
              ? convertToImperial(
                  userAttributes,
                  WearableTypeUnits[
                    userAttributes[0].type as keyof typeof WearableTypeUnits
                  ]
                )
              : userAttributes;
        }
        setUserAttributeDataList(userAttributeList);
      })();
    }
  }, [
    findUserAttributes,
    landscapeDayCount,
    maxStartDate,
    portraitDayCount,
    studyParticipant?.userId,
    user?.id,
    variant,
    wearableSearchConfigs
  ]);

  const getTotalSleepValue = useCallback(
    (results: Array<UserAttribute>, intendedStartDate: Date) => {
      if (results.length === 0) return undefined;
      const source = results[0].source;
      const dataCategory =
        source === UserAttributeSource.HealthKit
          ? ['CORE', 'REM', 'DEEP', 'ASLEEP', 'UNKNOWN']
          : undefined;
      const minStartDate = addDays(intendedStartDate, -1);
      minStartDate.setHours(17, 59, 59, 59);
      const maxStartDate = addDays(minStartDate, 1);
      const maxEndDate = addDays(minStartDate, 2);
      maxEndDate.setHours(0, 0, 0, 0);
      const newResults = results.filter(result => {
        const endDate = new Date(result.endDate);
        const startDate = new Date(result.startDate);
        return (
          result.inputType === UserAttributeInputType.Wearable &&
          (dataCategory
            ? dataCategory.includes(result.jsonValue.value)
            : true) &&
          isAfter(startDate, minStartDate) &&
          isBefore(startDate, maxStartDate) &&
          isBefore(endDate, maxEndDate)
        );
      });
      const uniqueResults: Array<UserAttribute> = [];
      newResults.forEach(data => {
        if (
          !uniqueResults.some(
            uniqueData =>
              uniqueData.startDate == data.startDate &&
              uniqueData.endDate == data.endDate
          )
        ) {
          uniqueResults.push(data);
        }
      });
      let userAttributes: UserAttribute | undefined = undefined;
      if (uniqueResults.length > 0) {
        userAttributes = {
          startDate: uniqueResults[uniqueResults.length - 1].startDate,
          endDate: uniqueResults[0].endDate,
          numberValue: uniqueResults.reduce(
            (prev, current) => prev + (current?.numberValue || 0),
            0
          )
        } as UserAttribute;
      }
      return userAttributes;
    },
    []
  );

  const getWearableDataPoints = useCallback(
    (
      dayCount: number,
      manualAttributeList: UserAttribute[],
      wearableAttributeList: UserAttribute[],
      type: UserAttributeType | OuraSpecificDataType,
      startDate: Date | number,
      isBloodPressure?: boolean
    ) => {
      if (!startDate)
        return { wearableDataPoints: [], maxY: 0, studyStartXPoint: undefined };
      let maxY = 0;
      let studyStartXPoint = undefined;
      const date = new Date(startDate);
      const days = differenceInDays(
        addDays(date, dayCount).setHours(0, 0, 0, 0),
        date.setHours(0, 0, 0, 0)
      );
      const wearableDataPoints: { x: number; y: number | number[] }[] = [];
      for (let i = 0; i < days; i++) {
        if (
          studyParticipant?.startDate &&
          isSameDay(studyParticipant.startDate, addDays(date, i))
        ) {
          studyStartXPoint = i;
        }
        if (!isBloodPressure) {
          let numberValue;
          numberValue = manualAttributeList?.find(ua =>
            isSameDay(addDays(date, i), ua.endDate)
          )?.numberValue;
          if (!numberValue && typeof numberValue === 'undefined') {
            if (type === UserAttributeType.Sleep) {
              numberValue = getTotalSleepValue(
                wearableAttributeList,
                addDays(date, i)
              )?.numberValue;
            } else if (type === OuraSpecificDataType.Workout) {
              const list = wearableAttributeList?.filter(ua =>
                isSameDay(addDays(date, i), ua.endDate)
              );
              numberValue =
                list && list.length > 0
                  ? list.reduce((a, v) => a + (v?.numberValue || 0), 0)
                  : undefined;
            } else {
              numberValue = wearableAttributeList?.find(ua =>
                isSameDay(addDays(date, i), ua.endDate)
              )?.numberValue;
            }
          }
          if (typeof numberValue !== 'undefined') {
            if (numberValue > maxY) maxY = numberValue;
            wearableDataPoints.push({ x: i, y: numberValue || 0 });
          }
        } else {
          let textValue = manualAttributeList?.find(ua =>
            isSameDay(addDays(date, i), ua.endDate)
          )?.textValue;
          if (!textValue && typeof textValue === 'undefined') {
            textValue = wearableAttributeList?.find(ua =>
              isSameDay(addDays(date, i), ua.endDate)
            )?.textValue;
          }
          const yArr = textValue?.split('/').map(Number);

          if (yArr && typeof yArr !== 'undefined' && yArr.length === 2) {
            const arrMax = Math.max(yArr[0], yArr[1]);
            if (arrMax > maxY) maxY = arrMax;
            wearableDataPoints.push({ x: i, y: yArr || [0, 0] });
          }
        }
      }
      return {
        wearableDataPoints,
        maxY: [
          UserAttributeType.OxygenSaturation,
          OuraSpecificDataType.SleepEfficiency
        ].includes(type)
          ? 100
          : type === UserAttributeType.Steps
          ? roundOffSteps(maxY)
          : Math.max(maxY, 0.5) * 1.1,
        isOxygenSaturation: type === UserAttributeType.OxygenSaturation,
        studyStartXPoint,
        roundValuesYAxis: ![
          OuraSpecificDataType.TemperatureDeviation,
          OuraSpecificDataType.TemperatureTrendDeviation
        ].includes(type as OuraSpecificDataType)
      };
    },
    [studyParticipant?.startDate, getTotalSleepValue]
  );

  const getWearableTypeList = (userAttributes: UserAttribute[]) => {
    const manualUserAttributeList: UserAttribute[] =
      userAttributes?.filter(
        ua => ua?.inputType === UserAttributeInputType.Manual
      ) || [];
    const wearableUserAttributeList: UserAttribute[] =
      userAttributes?.filter(
        ua => ua?.inputType === UserAttributeInputType.Wearable
      ) || [];
    return { wearableUserAttributeList, manualUserAttributeList };
  };

  //sets chart landscape Data based on tab selected
  const { landscapeDataPoints, landscapeDataUnit } = useMemo(() => {
    if (
      selectedWearableConfigIndex >= 0 &&
      landscapeStartDate &&
      userAttributeDataList.length !== 0
    ) {
      setIsLoading(true);
      const userAttributes =
        selectedWearableConfigIndex >= 0 &&
        selectedWearableConfigIndex < userAttributeDataList.length
          ? userAttributeDataList[selectedWearableConfigIndex].attributes
          : [];
      const unit =
        WearableTypeUnits[
          userAttributes[0]?.type as keyof typeof WearableTypeUnits
        ];
      const { manualUserAttributeList, wearableUserAttributeList } =
        getWearableTypeList(userAttributes);
      const landscapeDataPoints = getWearableDataPoints(
        landscapeDayCount,
        manualUserAttributeList,
        wearableUserAttributeList,
        wearableSearchConfigs[selectedWearableConfigIndex]?.config
          ?.dataSource as UserAttributeType, //change to selected One
        landscapeStartDate,
        unit === 'mmHg'
      );
      setIsLoading(false);

      return {
        landscapeDataPoints,
        landscapeDataUnit: getConvertedUnit(
          userAttributes.length > 0
            ? unit
            : (wearableSearchConfigs?.length > 0 &&
                (wearableSearchConfigs[0]?.unit ||
                  measurements(user?.unitSystem).find(
                    el =>
                      el.name ===
                        wearableSearchConfigs[0]?.config?.dataSource ||
                      (el.displayName &&
                        el.displayName ===
                          wearableSearchConfigs[0]?.config?.dataSource)
                  )?.unit)) ||
                ''
        )
      };
    }
    return {
      landscapeDataPoints: {
        wearableDataPoints: [],
        maxY: 0,
        isOxygenSaturation: false,
        studyStartXPoint: undefined,
        roundValuesYAxis: false
      }
    };
  }, [
    getWearableDataPoints,
    landscapeDayCount,
    landscapeStartDate,
    selectedWearableConfigIndex,
    userAttributeDataList,
    wearableSearchConfigs
  ]);

  //sets portrait data points
  const { portraitData, portraitDataUnit } = useMemo(() => {
    const userAttributes =
      userAttributeDataList[
        variant === 'study' ? 0 : selectedWearableConfigIndex
      ]?.attributes || [];
    const unit =
      WearableTypeUnits[
        userAttributes[0]?.type as keyof typeof WearableTypeUnits
      ];
    const { manualUserAttributeList, wearableUserAttributeList } =
      getWearableTypeList(userAttributes);

    let portraitData:
      | {
          wearableDataPoints: {
            x: number;
            y: number | number[];
          }[];
          maxY: number;
          isOxygenSaturation?: boolean;
          studyStartXPoint?: number;
          roundValuesYAxis?: boolean;
        }
      | undefined = {
      wearableDataPoints: [],
      maxY: 0,
      studyStartXPoint: undefined
    };
    if (wearableSearchConfigs.length > 0) {
      portraitData = getWearableDataPoints(
        portraitDayCount || 0,
        manualUserAttributeList,
        wearableUserAttributeList,
        wearableSearchConfigs[
          variant === 'study' ? 0 : selectedWearableConfigIndex
        ]?.config?.dataSource as UserAttributeType, //change to selected One
        portraitStartDate,
        unit === 'mmHg'
      );
    }
    return {
      portraitData:
        studyParticipant?.id || (variant === 'measurement' && user?.id)
          ? portraitData
          : sampleData(),
      portraitDataUnit: getConvertedUnit(
        studyParticipant?.id || (variant === 'measurement' && user?.id)
          ? userAttributes.length > 0
            ? unit
            : (wearableSearchConfigs?.length > 0 &&
                (wearableSearchConfigs[0]?.unit ||
                  measurements(user?.unitSystem).find(
                    el =>
                      el.name ===
                        wearableSearchConfigs[0]?.config?.dataSource ||
                      (el.displayName &&
                        el.displayName ===
                          wearableSearchConfigs[0]?.config?.dataSource)
                  )?.unit)) ||
              ''
          : 'h'
      )
    };
  }, [
    getWearableDataPoints,
    portraitDayCount,
    portraitStartDate,
    sampleData,
    selectedWearableConfigIndex,
    studyParticipant?.id,
    user?.id,
    userAttributeDataList,
    variant,
    wearableSearchConfigs
  ]);

  const selectedTab = useMemo(
    () => headerTabList[selectedWearableConfigIndex],
    [headerTabList, selectedWearableConfigIndex]
  );

  // build query from component configuration
  useEffect(() => {
    if (studyParticipant?.id && typeof selectedOption !== 'undefined') {
      const comparison = comparisons[selectedOption];
      if (
        comparison?.comparisonType === ComparisonType.Entity &&
        comparison?.columnName &&
        comparison?.entityId
      ) {
        queryData([
          {
            entityId: comparison.entityId,
            columnNames: [comparison.columnName]
          }
        ]);
      } else if (
        comparison?.comparisonType === ComparisonType.Wearable &&
        comparison?.userAttributeSource &&
        comparison?.userAttributeType &&
        maxStartDate
      ) {
        (async () => {
          const userAttributes =
            (await findUserAttributes(
              studyParticipant?.userId || -1,
              String(comparison?.userAttributeType),
              String(comparison?.userAttributeSource),
              undefined,
              undefined,
              undefined,
              undefined,
              landscapeDayCount
            )) || [];
          setCompareAttributes(
            user?.unitSystem === UserUnitSystem.Imperial
              ? convertToImperial(
                  userAttributes,
                  WearableTypeUnits[
                    userAttributes[0].type as keyof typeof WearableTypeUnits
                  ]
                )
              : userAttributes
          );
        })();
      }
    }
  }, [
    comparisons,
    findUserAttributes,
    landscapeDayCount,
    maxStartDate,
    queryData,
    selectedOption,
    setCompareAttributes,
    studyParticipant?.id,
    studyParticipant?.userId
  ]);

  const getDataAverage = (
    data: { x: number; y: number | number[] }[]
  ): number | number[] => {
    if (!data || !data.length) return 0;

    const firstY = data[0].y;
    if (!isArray(firstY)) {
      let total = 0;
      data.forEach(e => {
        total += e.y as number;
      });
      return total / data.length;
    }

    const yLength = firstY.length;
    const totals = Array(yLength).fill(0);

    data.forEach(item => {
      const arr = item.y as number[];
      for (let i = 0; i < yLength; i++) {
        totals[i] += arr[i];
      }
    });

    return totals.map(t => t / data.length);
  };

  const getOptions: () => SelectOption[] = () => {
    if (!comparisons || comparisons.length === 0) return [];
    return [
      { value: -1, label: 'None' },
      ...comparisons.map((value, index) => {
        return { value: index, label: value.name || value.columnName };
      })
    ];
  };

  // get compared data points
  const { compareDataSet, compareDataUnit, compareDataRoundYAxisValues } =
    useMemo(() => {
      if (
        queryResults &&
        queryResults.length > 0 &&
        queryResults[0].length > 0 &&
        typeof selectedOption !== 'undefined' &&
        landscapeStartDate &&
        comparisons[selectedOption].comparisonType === ComparisonType.Entity &&
        comparisons[selectedOption].entityId &&
        comparisons[selectedOption].columnName
      ) {
        const maxDate = addDays(landscapeStartDate, landscapeDayCount);
        const compareDataSet = queryResults[0]
          .filter(e => {
            const value = e[comparisons[selectedOption].columnName || ''];
            return value && isBefore(e.intendedStartDate, maxDate);
          })
          .map(row => {
            const intendedDate = row.intendedStartDate;
            const x = differenceInDays(intendedDate, landscapeStartDate) + 1; // add one day since intended startdate is set the day before
            const y = row[comparisons[selectedOption].columnName || ''];
            return {
              x,
              y
            };
          });
        return {
          compareDataSet,
          compareDataUnit: comparisons[selectedOption]?.unit || ''
        };
      } else if (
        typeof selectedOption !== 'undefined' &&
        comparisons[selectedOption].comparisonType ===
          ComparisonType.Wearable &&
        comparisons[selectedOption].userAttributeSource &&
        comparisons[selectedOption].userAttributeType &&
        compareAttributes.length > 0
      ) {
        const { manualUserAttributeList, wearableUserAttributeList } =
          getWearableTypeList(compareAttributes);

        const unit = getConvertedUnit(
          WearableTypeUnits[
            compareAttributes[0].type as keyof typeof WearableTypeUnits
          ] ||
            compareAttributes[0].unit ||
            ''
        );
        const compareDataPoints = getWearableDataPoints(
          landscapeDayCount,
          manualUserAttributeList,
          wearableUserAttributeList,
          comparisons[selectedOption].userAttributeType!,
          landscapeStartDate,
          unit === 'mmHg'
        );
        return {
          compareDataSet: compareDataPoints.wearableDataPoints,
          compareDataUnit: unit,
          compareDataRoundYAxisValues: compareDataPoints.roundValuesYAxis
        };
      }
      return { compareDataSet: [], compareMaxY: 0 };
    }, [
      compareAttributes,
      comparisons,
      getWearableDataPoints,
      landscapeDayCount,
      landscapeStartDate,
      queryResults,
      selectedOption
    ]);

  useEffect(() => {
    if (showModal) {
      enableLandscapeOrientationLock();
    } else {
      enablePortraitOrientationLock();
    }
  }, [showModal]);

  useEffect(() => {
    if (selectedWearableConfigs.length > 0 && isHeaderTabChanged) {
      AsyncStorage.setItem(
        'wearableConfigs',
        JSON.stringify(selectedWearableConfigs)
      );
    }
  }, [selectedWearableConfigs, isHeaderTabChanged]);

  const roundAverageValue = useMemo(
    () => portraitData.roundValuesYAxis || landscapeDataPoints.roundValuesYAxis,
    [landscapeDataPoints.roundValuesYAxis, portraitData.roundValuesYAxis]
  );

  const getLabel = (
    data: {
      x: number;
      y: number | number[];
    }[],
    unit?: string
  ) => {
    const average = getDataAverage(data);
    if (isArray(average)) {
      return `${average[0].toLocaleString(undefined, {
        maximumFractionDigits: roundAverageValue ? 0 : 2,
        minimumFractionDigits: 0
      })}/${average[1].toLocaleString(undefined, {
        maximumFractionDigits: roundAverageValue ? 0 : 2,
        minimumFractionDigits: 0
      })} ${unit} `;
    } else {
      return `${average.toLocaleString(undefined, {
        maximumFractionDigits: roundAverageValue
          ? 0
          : unit === '°F' || unit === '°C'
          ? 1
          : 2,
        minimumFractionDigits: 0
      })} ${unit || ''}`;
    }
  };

  return (
    <>
      {variant === 'measurement' && !showModal && !isLoading && (
        <HeaderTabView
          value={selectedTab}
          size='small'
          backgroundColor={Colors.background}
          removeBorder
          tabs={headerTabList}
          onTabChange={(index, tab) => {
            if (index < wearableSearchConfigs.length) {
              onSelectSource?.(
                wearableSearchConfigs[index].source,
                wearableSearchConfigs[index].subSource,
                wearableSearchConfigs[index].sourceName
              );
            } else {
              onSelectSource?.(
                tab === 'Health'
                  ? UserAttributeSource.HealthKit
                  : (tab as UserAttributeSource)
              );
            }
            setSelectedWearableConfigIndex(index);
            setSelectedWearableConfigs(prevConfigs => {
              const dataSource = wearableSearchConfigs[0]?.config?.dataSource;
              const source =
                wearableSearchConfigs[index]?.source ??
                (tab === 'Health'
                  ? UserAttributeSource.HealthKit
                  : (tab as UserAttributeSource));
              const sourceName =
                wearableSearchConfigs[index]?.sourceName ??
                (tab === 'Health' ? 'Health' : tab);
              const subSource = wearableSearchConfigs[index]?.subSource;
              const isTypeAvailable = prevConfigs.some(
                config => config.type === dataSource
              );
              // if the config for the selected tab is already set, then just change its source
              if (isTypeAvailable) {
                return prevConfigs.map(config =>
                  config.type === dataSource
                    ? { ...config, source, sourceName, subSource }
                    : config
                );
              }
              return [
                ...prevConfigs,
                {
                  source,
                  sourceName,
                  subSource,
                  type: dataSource as UserAttributeType
                }
              ];
            });
            setIsHeaderTabChanged(true);
          }}
        />
      )}
      <StyledView>
        <StyledTitleView>
          <Caption color='dark'>
            {title
              ? title
              : wearableSearchConfigs.length > 0
              ? wearableSearchConfigs[0]?.config?.dataSource
              : ''}
          </Caption>
          <Spacer size={1} />
          <H4 color='dark'>
            {portraitDataUnit === 'h'
              ? getHoursLabel(
                  getDataAverage(portraitData.wearableDataPoints) as number
                )
              : `${getLabel(
                  portraitData.wearableDataPoints,
                  portraitDataUnit
                )}`}{' '}
            (avg)
          </H4>
          {wearableSearchConfigs.length > 0 &&
            selectedWearableConfigIndex >= 0 && (
              <StyledHorizontalView>
                <MaterialIcons color='#949AAD' size={16} name='sync' />
                <StyledSyncText>
                  {' '}
                  {wearableSearchConfigs[selectedWearableConfigIndex]?.created
                    ? 'Last sync: ' +
                      format(
                        new Date(
                          wearableSearchConfigs[
                            selectedWearableConfigIndex
                          ].created!
                        ),
                        'MMM d, yyyy h:mm a'
                      )
                    : ''}
                </StyledSyncText>
              </StyledHorizontalView>
            )}
        </StyledTitleView>
        <StyledIconButton
          onPress={() => {
            setShowModal(true);
          }}
        >
          <Ionicons size={20} name='expand' />
        </StyledIconButton>
        <View style={{ width: '100%', height: 280 }}>
          <LineChart
            leftAxisDataUnit={portraitDataUnit}
            chartStartDate={portraitStartDate}
            dataPoints={[portraitData.wearableDataPoints, []]}
            xAxisMaxValueLabel={
              typeof portraitDayCount !== 'undefined' ? portraitDayCount : 0
            }
            domainYMin={portraitData.isOxygenSaturation ? 0.8 : undefined}
            yAxisMaxValueLabel={
              portraitData.isOxygenSaturation && portraitData.maxY !== 0
                ? 100
                : portraitData.maxY
            }
            roundValuesLeftAxis={portraitData?.roundValuesYAxis}
            {...props}
          />
        </View>
        {loadingQueryError && (
          <AlertBox fullWidth severity='error'>
            Chart error: {loadingQueryError.message}
          </AlertBox>
        )}
        <StyledBottomView>
          <Caption center color='dark'>
            Last {portraitDayCount} days
          </Caption>
        </StyledBottomView>
        <Modal visible={showModal} supportedOrientations={['landscape']}>
          <SafeAreaView>
            <StyledModalView>
              <StyledHeaderView>
                <StyledBackIconButton
                  onPress={() => {
                    setShowModal(false);
                    if (variant !== 'measurement') {
                      setSelectedWearableConfigIndex(0);
                    }
                  }}
                >
                  <Feather name='chevron-left' size={24} />
                </StyledBackIconButton>
                <H5 color='dark'>
                  {title
                    ? title
                    : wearableSearchConfigs.length > 0
                    ? wearableSearchConfigs[0]?.config?.dataSource
                    : ''}
                </H5>
              </StyledHeaderView>
              <HeaderTabView
                value={selectedTab}
                size='small'
                removeBorder
                tabs={headerTabList}
                onTabChange={(index, tab) => {
                  if (index < wearableSearchConfigs.length) {
                    onSelectSource?.(
                      wearableSearchConfigs[index].source,
                      wearableSearchConfigs[index].subSource,
                      wearableSearchConfigs[index].sourceName
                    );
                  } else {
                    onSelectSource?.(
                      tab === 'Health'
                        ? UserAttributeSource.HealthKit
                        : (tab as UserAttributeSource)
                    );
                  }
                  setSelectedWearableConfigIndex(index);
                  setSelectedWearableConfigs(prevConfigs => {
                    const dataSource =
                      wearableSearchConfigs[0]?.config?.dataSource;
                    const source =
                      wearableSearchConfigs[index]?.source ??
                      (tab === 'Health'
                        ? UserAttributeSource.HealthKit
                        : (tab as UserAttributeSource));
                    const sourceName =
                      wearableSearchConfigs[index]?.sourceName ??
                      (tab === 'Health' ? 'Health' : tab);
                    const subSource = wearableSearchConfigs[index]?.subSource;
                    const isTypeAvailable = prevConfigs.some(
                      config => config.type === dataSource
                    );
                    // if the config for the selected tab is already set, then just change its source
                    if (isTypeAvailable) {
                      return prevConfigs.map(config =>
                        config.type === dataSource
                          ? { ...config, source, sourceName, subSource }
                          : config
                      );
                    }
                    return [
                      ...prevConfigs,
                      {
                        source,
                        sourceName,
                        subSource,
                        type: dataSource as UserAttributeType
                      }
                    ];
                  });
                  setIsHeaderTabChanged(true);
                }}
              />
              <StyledView
                style={{
                  height: Platform.OS !== 'web' ? 256 : '100%'
                }}
              >
                <StyledModalInfoView>
                  <StyledModalInfoTextView>
                    <Caption color='dark'>
                      {title
                        ? title
                        : wearableSearchConfigs.length > 0
                        ? wearableSearchConfigs[
                            variant === 'measurement'
                              ? 0
                              : selectedWearableConfigIndex
                          ]?.config?.dataSource
                        : ''}
                    </Caption>
                    <Spacer size={1} />
                    <StyledHorizontalView>
                      <H5 color='dark'>
                        {landscapeDataUnit === 'h'
                          ? getHoursLabel(
                              getDataAverage(
                                landscapeDataPoints?.wearableDataPoints || []
                              ) as number
                            )
                          : getLabel(
                              landscapeDataPoints.wearableDataPoints,
                              landscapeDataUnit
                            )}{' '}
                        (avg){' '}
                      </H5>
                      {wearableSearchConfigs.length > 0 &&
                        selectedWearableConfigIndex >= 0 && (
                          <StyledHorizontalView>
                            <MaterialIcons
                              color='#949AAD'
                              size={16}
                              name='sync'
                            />
                            <StyledSyncText>
                              {' '}
                              {wearableSearchConfigs[
                                selectedWearableConfigIndex
                              ]?.created
                                ? 'Last sync: ' +
                                  format(
                                    new Date(
                                      wearableSearchConfigs[
                                        selectedWearableConfigIndex
                                      ].created!
                                    ),
                                    'MMM d, yyyy h:mm a'
                                  )
                                : ''}
                            </StyledSyncText>
                          </StyledHorizontalView>
                        )}
                    </StyledHorizontalView>
                  </StyledModalInfoTextView>
                  {variant === 'study' &&
                    !embedded &&
                    comparisons.length !== 0 && (
                      <StyledSelectFieldView>
                        <SelectField
                          onChange={option => {
                            setCompareAttributes([]);
                            setSelectedOption(
                              typeof option === 'number'
                                ? option
                                : (option as SelectOption).value !== -1
                                ? Number((option as SelectOption).value)
                                : undefined
                            );
                          }}
                          variant='chart'
                          placeholder='compare'
                          value={selectedOption}
                          options={getOptions()}
                        />
                      </StyledSelectFieldView>
                    )}
                </StyledModalInfoView>
                <LineChart
                  expanded
                  dataPointSelectMode='exact'
                  rightAxisDataUnit={compareDataUnit}
                  enableRightAxis={typeof selectedOption !== 'undefined'}
                  leftAxisDataUnit={landscapeDataUnit}
                  chartStartDate={landscapeStartDate}
                  studyStartXPoint={landscapeDataPoints.studyStartXPoint}
                  dataPoints={[
                    landscapeDataPoints?.wearableDataPoints || [],
                    compareDataSet
                  ]}
                  xAxisMaxValueLabel={
                    typeof landscapeDayCount !== 'undefined'
                      ? landscapeDayCount
                      : 0
                  }
                  rightAxisDomainYMin={
                    typeof selectedOption === 'number'
                      ? comparisons[selectedOption].userAttributeType ===
                        UserAttributeType.OxygenSaturation
                        ? 0.8
                        : undefined
                      : undefined
                  }
                  rightAxisYMaxValueLabel={
                    typeof selectedOption === 'number'
                      ? comparisons[selectedOption].userAttributeType ===
                        UserAttributeType.OxygenSaturation
                        ? 100
                        : undefined
                      : undefined
                  }
                  domainYMin={
                    landscapeDataPoints?.isOxygenSaturation ? 0.8 : undefined
                  }
                  yAxisMaxValueLabel={
                    landscapeDataPoints?.isOxygenSaturation &&
                    landscapeDataPoints?.maxY !== 0
                      ? 100
                      : landscapeDataPoints?.maxY || 0
                  }
                  roundValuesLeftAxis={landscapeDataPoints?.roundValuesYAxis}
                  roundValuesRightAxis={compareDataRoundYAxisValues}
                  {...props}
                />
              </StyledView>
              {(isLoading ||
                loadingUserAttributes ||
                loadingQuery ||
                loading) && <ActivityIndicatorScreen modal />}
            </StyledModalView>
          </SafeAreaView>
        </Modal>
        {(isLoading || loadingUserAttributes || loadingQuery) && !showModal && (
          <ActivityIndicatorScreen modal />
        )}
      </StyledView>
    </>
  );
};

export const WearableDataLineChartCardPlugin: ScreenComponentPlugin<WearableDataLineChartCardProps> =
  {
    name: 'Wearable Data Line Chart Card',
    type: 'WearableDataLineChartCard',
    categories: ['my-data'],
    component: WearableDataLineChartCard
  };
